import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { dashboardApi, type DashboardData, type DashboardRecentRequest } from '../features/dashboard/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { canFinance, canProcure } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';

function fmtMvr(n: number): string {
  return `MVR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusPill(status: DashboardRecentRequest['status']): string {
  switch (status) {
    case 'DRAFT': return 'pill pill-neutral';
    case 'SENT': return 'pill pill-pending';
    case 'CLOSED': return 'pill pill-success';
  }
}
function statusLabel(status: DashboardRecentRequest['status']): string {
  switch (status) {
    case 'DRAFT': return t.dashboard.statusDraft;
    case 'SENT': return t.dashboard.statusSent;
    case 'CLOSED': return t.dashboard.statusClosed;
  }
}

/* WapSign's stats-banner vocabulary, verbatim: a chrome-tinted rounded-2xl card
   holding a tonal round icon chip beside a small label, with the figure set
   large and light underneath — and WapSign's own narrow-screen treatment, where
   three cards scroll-snap sideways rather than wrapping to a ragged second row. */
const BANNER = 'flex gap-3 -mx-4 px-4 pb-1 overflow-x-auto snap-x [scrollbar-width:none] sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible';
const CARD = 'shrink-0 min-w-[43%] snap-start rounded-2xl border border-black/[0.04] bg-[hsl(var(--chrome))] p-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:min-w-0 sm:shrink sm:p-5';
const CHIP = 'grid size-9 sm:size-10 shrink-0 place-items-center rounded-full';
const CARD_LABEL = 'text-[0.8rem] sm:text-sm font-medium leading-tight text-foreground/70';
const CARD_NUM = 'mt-3 text-[2rem] sm:text-[2.5rem] font-medium leading-none tabular num text-ink';

export function Dashboard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const showFinance = canFinance(user?.role);
  const showCreate = canProcure(user?.role);

  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.get(),
  });

  /* The three figures that represent work sitting on someone's desk — WapSign's
     "Awaiting you" logic. Finance owns payment, so staff get the open-GSR count
     as their third card instead of an empty slot. */
  const cards = data
    ? [
      {
        to: '/requests',
        label: t.dashboard.readyToAward,
        value: data.readyToAward,
        icon: 'check_box',
        chip: 'bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]',
        note: null as string | null,
      },
      {
        to: '/requests',
        label: t.dashboard.activeRfqs,
        value: data.activeRfqs,
        icon: 'inbox',
        chip: 'bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))]',
        note: null as string | null,
      },
      showFinance
        ? {
          to: '/payments',
          label: t.dashboard.awaitingPayment,
          value: data.awaitingPaymentCount,
          icon: 'wallet',
          chip: 'bg-[hsl(var(--warning-container))] text-[hsl(var(--on-warning-container))]',
          note: data.awaitingPaymentTotal > 0 ? fmtMvr(data.awaitingPaymentTotal) : null,
        }
        : {
          to: '/gsr',
          label: t.dashboard.gsrsOpen,
          value: data.gsrsOpen,
          icon: 'description',
          chip: 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]',
          note: null as string | null,
        },
    ]
    : [];

  return (
    <div className="page space-y-5 md:space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t.dashboard.welcome} {user?.nameDv || user?.name || ''}</p>
          <h1 className="mt-1 font-serif text-3xl leading-tight tracking-tight md:text-[2.5rem]">{t.dashboard.heading}</h1>
        </div>
        {/* Also the only way to start a request on mobile — the sidebar that
            carries this action is desktop-only. */}
        {showCreate && (
          <Link to="/gsr/new" className="btn-primary">
            <Icon name="add" size={20} />
            {t.requests.newRequest}
          </Link>
        )}
      </header>

      {isLoading || !data ? (
        <p role="status" aria-live="polite" className="status-label">{t.dashboard.loading}</p>
      ) : (
        <>
          <section className={BANNER}>
            {cards.map((c) => (
              <Link key={c.label} to={c.to} className={CARD}>
                <div className="flex items-center gap-2.5">
                  <span className={`${CHIP} ${c.chip}`}><Icon name={c.icon} size={20} /></span>
                  <p className={CARD_LABEL}>{c.label}</p>
                </div>
                <p className={CARD_NUM}>{c.value}</p>
                {c.note && (
                  <p className="mt-2 hidden text-xs text-foreground/60 sm:block" dir="ltr">{c.note}</p>
                )}
              </Link>
            ))}
          </section>

          <section className="space-y-2">
            <h2 className="status-label">{t.dashboard.recentHeading}</h2>
            {data.recentRequests.length === 0 ? (
              <div className="card-paper space-y-3 p-10 text-center">
                <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                  <Icon name="inbox" size={22} />
                </div>
                <p className="font-serif text-2xl text-ink">{t.dashboard.noRequests}</p>
                {showCreate && <Link to="/gsr/new" className="btn-secondary">{t.requests.newRequest}</Link>}
              </div>
            ) : (
              <ul className="overflow-hidden rounded-3xl bg-[hsl(var(--chrome))]">
                {data.recentRequests.map((r) => (
                  <li key={r.id} className="border-b border-black/[0.05] last:border-b-0 dark:border-white/[0.06]">
                    <Link
                      to="/requests/$requestId"
                      params={{ requestId: r.id }}
                      className="flex items-center gap-4 px-4 py-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
                    >
                      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                        <Icon name="description" size={22} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[0.95rem] font-medium text-ink">{r.title}</div>
                        <div className="mono-meta num text-muted-foreground">{r.refNumber}</div>
                      </div>
                      <span className={statusPill(r.status)}>{statusLabel(r.status)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
